import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
const areaCodes = [{
  code: '253',
  src: 'https://cdn.magicpatterns.com/uploads/68rE1RFxKTaMLnZX9bTQ6m/253.png',
  duration: 20000
}, {
  code: '206',
  src: 'https://cdn.magicpatterns.com/uploads/6RRJ8Cb2MtYWCN2KCSKtBN/206.png',
  duration: 500
}, {
  code: '425',
  src: 'https://cdn.magicpatterns.com/uploads/g4GXqv7wQ4H6HHSd1TBZ5s/425.png',
  duration: 500
}];
const GRAVEL_SRC = 'https://cdn.magicpatterns.com/uploads/tqXEcbNCcYC2dBxg9zT8wG/GRAVEL.png';
interface LogoProps {
  className?: string;
  imgClassName?: string;
  invertOnLight?: boolean;
  /**
   * Show every Nth full cycle. 1 = animate every cycle (default, used by navbar).
   * 3 = only animate every 3rd cycle (used by footer).
   * Between rotations, 253 holds for `cycleEvery * (full cycle duration)`.
   */
  cycleEvery?: number;
}
/**
 * Logo lockup with a static "Gravel" wordmark layered on top of a rotating area code.
 * Default cycle: 253 (10s) → 206 (1s) → 425 (1s) → repeat.
 * Area code slides up between values (0.25s); "Gravel" stays static.
 */
export function Logo({
  className = '',
  imgClassName = 'h-10 w-auto',
  invertOnLight = true,
  cycleEvery = 1
}: LogoProps) {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    // When at 253, multiply hold by cycleEvery so the rotation only happens
    // every Nth cycle relative to the default cadence.
    const baseDuration = areaCodes[index].duration;
    const duration = index === 0 ? baseDuration * cycleEvery : baseDuration;
    const timeout = setTimeout(() => {
      setIndex(i => (i + 1) % areaCodes.length);
    }, duration);
    return () => clearTimeout(timeout);
  }, [index, cycleEvery]);
  const current = areaCodes[index];
  const invertClass = invertOnLight ? 'dark:brightness-100 brightness-0' : '';
  return <span className={`relative inline-block overflow-hidden ${className}`} aria-label={`${current.code}Gravel`} data-id="element-673">
      {/* Sizer: GRAVEL defines the layout dimensions and stays static on top */}
      <img src={GRAVEL_SRC} alt="" aria-hidden="true" className={`${imgClassName} ${invertClass} block relative z-10 pointer-events-none`} data-id="element-674" />

      {/* Rotating area code, layered underneath GRAVEL with slide-up transition */}
      <AnimatePresence initial={false} data-id="element-675">
        <motion.img key={current.code} src={current.src} alt={current.code} className={`${imgClassName} ${invertClass} absolute inset-0 z-0`} initial={{
        y: '100%'
      }} animate={{
        y: '0%'
      }} exit={{
        y: '-100%'
      }} transition={{
        duration: 0.25,
        ease: [0.16, 1, 0.3, 1]
      }} data-id="element-676" />
      </AnimatePresence>
    </span>;
}